import React, { useEffect, useState } from 'react'
import classes from "./CreatePrediction2.module.css";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from "moment-timezone";
import { editUserPredictionsApi, fetchUpcomingMatchDetailsApi, getUserPredictionsApi, saveUserPredictionsApi } from '../../http/http';
import { FaArrowLeft, FaMinus, FaXmark } from 'react-icons/fa6';
import { Dropdown } from "primereact/dropdown";
import { toast } from 'react-toastify';
import SmallLoader from '../../component/SmallLoader';
import { removeUserDetails } from '../../store/slices/userSlice';

const HOW_TO_PLAY_PREDICTION = [
    {
        icon: "🏆",
        points: "10 pts",
        title: "Match Result",
        desc: "Predict which team wins — or if it's a draw. Correct = 10 points.",
        color: "blue",
    },
    {
        icon: "⚽",
        points: "10 pts each",
        title: "Goals Scored",
        desc: "Predict how many goals Team A and Team B will each score. 10 points per correct answer.",
        color: "green",
    },
    {
        icon: "🎯",
        points: "20 pts",
        title: "Goal Scorer",
        desc: "Pick the player who scores + how many goals. 10 pts for the right player, 10 pts for the right tally.",
        color: "gold",
    },
    {
        icon: "🟥",
        points: "20 pts",
        title: "Red Card",
        desc: "Predict which player gets a red card (or 'None'). Correct = 20 points.",
        color: "red",
    },
    {
        icon: "🟨",
        points: "20 pts",
        title: "Yellow Card",
        desc: "Predict which player gets a yellow card (or 'None'). Correct = 20 points.",
        color: "gold",
    },
    {
        icon: "🧤",
        points: "20 pts",
        title: "Goalkeeper Saves",
        desc: "Pick a goalkeeper + how many saves. 10 pts for right keeper, 10 pts for right save count.",
        color: "green",
    },
    {
        icon: "🚫",
        points: "20 pts",
        title: "Goals Conceded",
        desc: "Pick a goalkeeper + goals conceded. 10 pts for right keeper, 10 pts for right count.",
        color: "red",
    },
];

const CreatePrediction2 = () => {
    const location = useLocation();
    const [matchDetails, setMatchDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loader, setLoader] = useState(true);
    const navigate = useNavigate();
    const { userDetails } = useSelector((state) => state.persisted.userSlice);
    const [players, setPlayers] = useState(null);
    const [time, setTime] = useState(null);
    const [attackers, setAttackers] = useState([]);
    const [defenders, setDefenders] = useState([]);
    const [midfielders, setMidfielders] = useState([]);
    const [goalkeeper, setGoalkeeper] = useState([]);
    const [homeTeamLineups, setHomeTeamLineups] = useState([]);
    const [awayTeamLineups, setAwayTeamLineups] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [predictionLoading, setPredictionLoading] = useState(false);
    const [playModal, setPlayModal] = useState(false);

    const [selectedOption1, setSelectedOption1] = useState(null);
    const [selectedOption2, setSelectedOption2] = useState(null);
    const [selectedOption3, setSelectedOption3] = useState(null);
    const [selectedOption4, setSelectedOption4] = useState(null);
    const [selectedOption5, setSelectedOption5] = useState(null);
    const [selectedOption6, setSelectedOption6] = useState(null);
    const [selectedOption7, setSelectedOption7] = useState(null);
    const [selectedOption8, setSelectedOption8] = useState(null);

    const [selectedOption_4, setSelectedOption_4] = useState({});
    const [selectedOption_7, setSelectedOption_7] = useState({});
    const [selectedOption_8, setSelectedOption_8] = useState({});

    const dispatch = useDispatch();

    const matchTimer = (match_date_time) => {
        try {
            const liveTime = moment();
            const matchTime = moment.utc(match_date_time);
            const timeLeft = moment.duration(matchTime.diff(liveTime));
            if (timeLeft.asMilliseconds() <= 0) {
                navigate("/home");
                toast.info("Match Started!, You cannot make predictions now!");
            }
            const hours = Math.floor(timeLeft.asHours());
            const minutes = timeLeft.minutes();
            const seconds = timeLeft.seconds();
            setTime(`${hours}h ${minutes}m ${seconds}s`);
        } catch (error) { console.log(error, 'error--'); }
    };

    useEffect(() => {
        if (matchDetails) {
            const intervalId = setInterval(() => matchTimer(matchDetails?.match_date_time), 1000);
            return () => clearInterval(intervalId);
        }
    }, [matchDetails]);

    const fetchMatchDetails = async (match_fixture_id, load) => {
        try {
            if (load) setLoading(true);
            setLoader(true);
            const response = await fetchUpcomingMatchDetailsApi(match_fixture_id, userDetails?.token);
            setMatchDetails(response?.data);
            setPlayers({
                homeTeam: JSON.parse(response?.data?.home_team_players),
                awayTeam: JSON.parse(response?.data?.away_team_players)
            });
            setQuestions(JSON.parse(response?.data?.prediction_questions));
            setLoading(false);
            setLoader(false);
        } catch (error) {
            if (error?.response?.status == 403) {
                dispatch(removeUserDetails());
                toast.error("Login Again!");
                navigate('/');
            } else {
                setLoader(false);
                setLoading(false);
                toast.error(error?.response?.data?.message || error?.response?.message || error?.data?.message || "Something Went Wrong!");
            }
        }
    };

    const fetchUserPredictions = async () => {
        try {
            setLoading(true);
            const response = await getUserPredictionsApi(location?.state?.match_fixture_id, userDetails?.token);
            const userPredictions = JSON.parse(response?.data?.[0]?.predictions);
            userPredictions.forEach((prediction) => {
                const predictionKey = Object.keys(prediction)[0];
                switch (predictionKey) {
                    case "prediction1": setSelectedOption1(prediction[predictionKey]); break;
                    case "prediction2": setSelectedOption2(prediction[predictionKey]); break;
                    case "prediction3": setSelectedOption3(prediction[predictionKey]); break;
                    case "prediction4":
                        setSelectedOption4(prediction[predictionKey][0]);
                        setSelectedOption_4(prediction[predictionKey][1]);
                        break;
                    case "prediction5": setSelectedOption5(prediction[predictionKey]); break;
                    case "prediction6": setSelectedOption6(prediction[predictionKey]); break;
                    case "prediction7":
                        setSelectedOption7(prediction[predictionKey][0]);
                        setSelectedOption_7(prediction[predictionKey][1]);
                        break;
                    case "prediction8":
                        setSelectedOption8(prediction[predictionKey][0]);
                        setSelectedOption_8(prediction[predictionKey][1]);
                        break;
                    default: break;
                }
            });
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error, 'error---');
        }
    };

    useEffect(() => {
        if (location?.state?.match_fixture_id) {
            fetchMatchDetails(location?.state?.match_fixture_id, true);
            if (location?.state?.edit) fetchUserPredictions();
        } else {
            navigate("/home");
        }
    }, [location?.state?.match_fixture_id]);

    useEffect(() => {
        if (!loader) {
            const intervalId = setInterval(() => {
                if (!document.hidden) fetchMatchDetails(location?.state?.match_fixture_id, false);
            }, 10000);
            return () => clearInterval(intervalId);
        }
    }, [loader, location?.state?.match_fixture_id]);

    useEffect(() => {
        const home = players?.homeTeam || {};
        const away = players?.awayTeam || {};
        setAttackers([...(home.attackers || []), ...(away.attackers || [])]);
        setDefenders([...(home.defenders || []), ...(away.defenders || [])]);
        setMidfielders([...(home.midfielders || []), ...(away.midfielders || [])]);
        setGoalkeeper([...(home.goalkeepers || []), ...(away.goalkeepers || [])]);
        setAllPlayers([
            ...(home.attackers || []), ...(home.defenders || []),
            ...(home.midfielders || []), ...(home.goalkeepers || []),
            ...(away.attackers || []), ...(away.defenders || []),
            ...(away.midfielders || []), ...(away.goalkeepers || [])
        ]);
    }, [players]);

    const savePredictionHandler = async () => {
        try {
            setPredictionLoading(true);
            const predictions = [
                selectedOption1 && { prediction1: selectedOption1 },
                selectedOption2 && { prediction2: selectedOption2 },
                selectedOption3 && { prediction3: selectedOption3 },
                selectedOption4 && { prediction4: [selectedOption4, selectedOption_4] },
                selectedOption5 && { prediction5: selectedOption5 },
                selectedOption6 && { prediction6: selectedOption6 },
                selectedOption7 && { prediction7: [selectedOption7, selectedOption_7] },
                selectedOption8 && { prediction8: [selectedOption8, selectedOption_8] },
            ];
            if (location?.state?.edit) {
                await editUserPredictionsApi(matchDetails?.match_fixture_id, predictions,
                    matchDetails?.match_date_time, matchDetails?.match_venue_name, matchDetails?.match_venue_city,
                    matchDetails?.league_id, matchDetails?.league_name, matchDetails?.league_image,
                    matchDetails?.home_team_id, matchDetails?.home_team_name, matchDetails?.home_team_image,
                    matchDetails?.away_team_id, matchDetails?.away_team_name, matchDetails?.away_team_image, userDetails?.token);
            } else {
                await saveUserPredictionsApi(matchDetails?.match_fixture_id, predictions,
                    matchDetails?.match_date_time, matchDetails?.match_venue_name, matchDetails?.match_venue_city,
                    matchDetails?.league_id, matchDetails?.league_name, matchDetails?.league_image,
                    matchDetails?.home_team_id, matchDetails?.home_team_name, matchDetails?.home_team_image,
                    matchDetails?.away_team_id, matchDetails?.away_team_name, matchDetails?.away_team_image, userDetails?.token);
            }
            setPredictionLoading(false);
            navigate("/prediction-game", { state: { match_fixture_id: matchDetails?.match_fixture_id } });
        } catch (error) {
            setPredictionLoading(false);
            if (error?.response?.status == 403) {
                dispatch(removeUserDetails());
                toast.error("Login Again!");
                navigate('/');
            } else {
                toast.error(error?.response?.data?.message || error?.response?.message || error?.data?.message || "Something Went Wrong!");
            }
        }
    };

    const itemTemplate = (option) => (
        <div className={classes.selector}>
            <span>{option.answer}</span>
            {option.playing && (
                <span className={classes.highlight}>
                    <i className={`fa fa-circle ${classes.text_danger_glow} ${classes.blink}`} style={{ fontSize: "8px" }} />
                    Playing
                </span>
            )}
        </div>
    );

    const canSubmit = selectedOption1 && selectedOption2 && selectedOption3 && selectedOption4
        && selectedOption5 && selectedOption6 && selectedOption7 && selectedOption8;

    const answeredCount = [selectedOption1, selectedOption2, selectedOption3, selectedOption4,
        selectedOption5, selectedOption6, selectedOption7, selectedOption8].filter(Boolean).length;

    const goalOptions = (qId) => Array.from({ length: 11 }, (_, i) => ({
        answer: i === 0 ? "0" : i,
        questionId: qId,
        question: questions.find(q => q.id == qId)?.name ?? "",
        point: 0,
        message: "",
    }));

    const playerOption = (data, qId, qName) => ({
        answer: data?.name,
        questionId: qId,
        question: qName,
        point: 0,
        playerId: data?.id,
        message: "",
        playing: homeTeamLineups.length > 0 && awayTeamLineups.length > 0
            ? (homeTeamLineups[0]?.startXI?.find(d => d?.player?.id == data?.id) ||
               awayTeamLineups[0]?.startXI?.find(d => d?.player?.id == data?.id))
            : false,
    });

    return (
        <div className={classes.page}>
            <div className={classes.pageInner}>

                {loading ? (
                    <div className={classes.loaderWrap}><SmallLoader /></div>
                ) : (
                    <>
                        {/* Sticky header */}
                        <div className={classes.header}>
                            <div className={classes.header_nav}>
                                <div className={classes.header_nav_container}>
                                    <button type="button" className={classes.back_button} onClick={() => navigate("/home")}>
                                        <FaArrowLeft className={classes.back_icon} />
                                    </button>
                                    <div className={classes.header_text_container}>
                                        <span className={classes.header_text}>Predict &amp; Win</span>
                                        {time && <span className={classes.header_timer}>⏱ {time} left</span>}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={classes.header_button}
                                    onClick={() => setPlayModal(true)}
                                >
                                    How to play?
                                </button>
                            </div>

                            {/* Match strip */}
                            <div className={classes.match_details_container}>
                                <div className={classes.match_details}>
                                    <div className={classes.team}>
                                        <img src={matchDetails?.home_team_image} alt={matchDetails?.home_team_name} className={classes.team_logo} />
                                        <p className={classes.team_name}>{matchDetails?.home_team_name}</p>
                                    </div>
                                    <div className={classes.divider_container}>
                                        <FaMinus className={classes.divider} />
                                    </div>
                                    <div className={classes.team}>
                                        <img src={matchDetails?.away_team_image} alt={matchDetails?.away_team_name} className={classes.team_logo} />
                                        <p className={classes.team_name}>{matchDetails?.away_team_name}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Hint bar */}
                            <div className={classes.tab_info}>
                                <p className={classes.tab_info_text}>Predict the questions below</p>
                            </div>
                        </div>

                        {/* Questions */}
                        <div className={classes.questionsContent}>
                            {questions?.length > 0 && questions.map((question, qi) => (
                                <div className={classes.question} key={question?.id} style={{ animationDelay: `${qi * 0.05}s` }}>
                                    <p className={classes.question_title}>{question?.name}</p>

                                    {question.id == 1 && (
                                        <Dropdown filter value={selectedOption1} onChange={(e) => setSelectedOption1(e.value)}
                                            options={[
                                                { answer: matchDetails?.home_team_name, questionId: 1, question: question?.name, point: 0, message: "" },
                                                { answer: matchDetails?.away_team_name, questionId: 1, question: question?.name, point: 0, message: "" },
                                                { answer: "Draw", questionId: 1, question: question?.name, point: 0, message: "" },
                                            ]}
                                            optionLabel="answer" placeholder="Select Team" id="dropdown_input" />
                                    )}

                                    {question.id == 2 && (
                                        <Dropdown filter value={selectedOption2} onChange={(e) => setSelectedOption2(e.value)}
                                            options={goalOptions(2)} optionLabel="answer" placeholder="Select Goals" className="w-full md:w-14rem" />
                                    )}

                                    {question.id == 3 && (
                                        <Dropdown filter value={selectedOption3} onChange={(e) => setSelectedOption3(e.value)}
                                            options={goalOptions(3)} optionLabel="answer" placeholder="Select Goals" className="w-full md:w-14rem" />
                                    )}

                                    {question.id == 4 && (
                                        <>
                                            <Dropdown filter value={selectedOption4} onChange={(e) => setSelectedOption4(e.value)}
                                                options={[
                                                    { answer: "None", questionId: 4, point: 0, question: question?.name, playerId: null, message: "", playing: false },
                                                    ...allPlayers.map(d => playerOption(d, 4, question?.name))
                                                ]}
                                                virtualScrollerOptions={{ itemSize: 38 }} optionLabel="answer"
                                                placeholder="Select Player" className="w-full md:w-14rem" itemTemplate={itemTemplate} />
                                            {selectedOption4?.answer !== "None" && (
                                                <Dropdown filter value={selectedOption_4} onChange={(e) => setSelectedOption_4(e.value)}
                                                    options={goalOptions(4)} optionLabel="answer" placeholder="Select Goals" className="w-full md:w-14rem" />
                                            )}
                                        </>
                                    )}

                                    {question.id == 5 && (
                                        <Dropdown filter value={selectedOption5} onChange={(e) => setSelectedOption5(e.value)}
                                            options={[
                                                { answer: "None", questionId: 5, point: 0, question: question?.name, playerId: null, message: "", playing: false },
                                                ...allPlayers.map(d => playerOption(d, 5, question?.name))
                                            ]}
                                            virtualScrollerOptions={{ itemSize: 38 }} optionLabel="answer"
                                            placeholder="Select Player" className="w-full md:w-14rem" itemTemplate={itemTemplate} />
                                    )}

                                    {question.id == 6 && (
                                        <Dropdown filter value={selectedOption6} onChange={(e) => setSelectedOption6(e.value)}
                                            options={[
                                                { answer: "None", questionId: 6, point: 0, question: question?.name, playerId: null, message: "", playing: false },
                                                ...allPlayers.map(d => playerOption(d, 6, question?.name))
                                            ]}
                                            virtualScrollerOptions={{ itemSize: 38 }} optionLabel="answer"
                                            placeholder="Select Player" className="w-full md:w-14rem" itemTemplate={itemTemplate} />
                                    )}

                                    {question.id == 7 && (
                                        <>
                                            <Dropdown filter value={selectedOption7} onChange={(e) => setSelectedOption7(e.value)}
                                                options={goalkeeper.map(d => playerOption(d, 7, question?.name))}
                                                virtualScrollerOptions={{ itemSize: 38 }} optionLabel="answer"
                                                placeholder="Select Player" className="w-full md:w-14rem" itemTemplate={itemTemplate} />
                                            <Dropdown filter value={selectedOption_7} onChange={(e) => setSelectedOption_7(e.value)}
                                                options={goalOptions(7)} optionLabel="answer" placeholder="Select Goals" className="w-full md:w-14rem" />
                                        </>
                                    )}

                                    {question.id == 8 && (
                                        <>
                                            <Dropdown filter value={selectedOption8} onChange={(e) => setSelectedOption8(e.value)}
                                                options={goalkeeper.map(d => playerOption(d, 8, question?.name))}
                                                virtualScrollerOptions={{ itemSize: 38 }} optionLabel="answer"
                                                placeholder="Select Player" className="w-full md:w-14rem" itemTemplate={itemTemplate} />
                                            <Dropdown filter value={selectedOption_8} onChange={(e) => setSelectedOption_8(e.value)}
                                                options={goalOptions(8)} optionLabel="answer" placeholder="Select Goals" className="w-full md:w-14rem" />
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Sticky submit */}
                        <div className={classes.nextWrap}>
                            <div className={classes.nextInner}>
                                <span className={classes.nextStatus}>
                                    {answeredCount}/8 answered
                                </span>
                                <button
                                    type="button"
                                    className={`${classes.next_button} ${canSubmit ? classes.active : ''}`}
                                    disabled={predictionLoading || !canSubmit}
                                    onClick={() => { if (!predictionLoading && canSubmit) savePredictionHandler(); }}
                                >
                                    {predictionLoading ? <SmallLoader /> : location?.state?.edit ? "SAVE" : "JOIN →"}
                                </button>
                            </div>
                        </div>

                        {/* ── How To Play Modal ── */}
                        {playModal && (
                            <div className={classes.modalOverlay} onClick={() => setPlayModal(false)}>
                                <div className={classes.modalSheet} onClick={(e) => e.stopPropagation()}>

                                    {/* Modal header */}
                                    <div className={classes.modalHeader}>
                                        <div className={classes.modalHeaderLeft}>
                                            <span className={classes.modalEyebrow}>Predict &amp; Win — Point System</span>
                                            <h2 className={classes.modalTitle}>How To Play?</h2>
                                        </div>
                                        <button
                                            type="button"
                                            className={classes.modalCloseBtn}
                                            onClick={() => setPlayModal(false)}
                                        >
                                            <FaXmark className={classes.modalCloseIcon} />
                                        </button>
                                    </div>

                                    {/* Max points banner */}
                                    <div className={classes.modalBanner}>
                                        <span className={classes.bannerLabel}>Max possible score</span>
                                        <span className={classes.bannerScore}>130 pts</span>
                                        <span className={classes.bannerSub}>Answer all 8 questions correctly</span>
                                    </div>

                                    {/* Steps */}
                                    <div className={classes.modalBody}>
                                        {HOW_TO_PLAY_PREDICTION.map((step, i) => (
                                            <div key={i} className={classes.stepRow}>
                                                <div className={classes.stepIconWrap}>
                                                    <div className={`${classes.stepIconBox} ${classes[`stepIcon_${step.color}`]}`}>
                                                        <span className={classes.stepEmoji}>{step.icon}</span>
                                                    </div>
                                                    {i < HOW_TO_PLAY_PREDICTION.length - 1 && (
                                                        <div className={classes.stepLine} />
                                                    )}
                                                </div>
                                                <div className={classes.stepContent}>
                                                    <div className={classes.stepTitleRow}>
                                                        <span className={classes.stepTitle}>{step.title}</span>
                                                        <span className={classes.stepPoints}>{step.points}</span>
                                                    </div>
                                                    <span className={classes.stepDesc}>{step.desc}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Footer CTA */}
                                    <div className={classes.modalFooter}>
                                        <button
                                            type="button"
                                            className={classes.modalGotItBtn}
                                            onClick={() => setPlayModal(false)}
                                        >
                                            Got it, start predicting!
                                        </button>
                                    </div>

                                </div>
                            </div>
                        )}
                    </>
                )}

            </div>
        </div>
    );
};

export default CreatePrediction2;